// @ts-check
import { Table, TableCell, TableHeader, TableRow } from "@tiptap/extension-table"

export { TableCell, TableHeader, TableRow }
import { Fragment } from "@tiptap/pm/model"
import { getHTMLFromFragment } from "@tiptap/core"

function childNodes(node) {
  return node?.content?.content ?? []
}

function hasSpan(node) {
  return node.attrs.colspan > 1 || node.attrs.rowspan > 1
}

/** Mirrors tiptap-markdown's own check (see node_modules/tiptap-markdown table.js) —
 * a table can only round-trip through markdown if it's a single header row
 * followed by plain body rows, with no merged cells or multi-paragraph cells. */
function isMarkdownSerializable(node) {
  const rows = childNodes(node)
  const firstRow = rows[0]
  const bodyRows = rows.slice(1)

  if (childNodes(firstRow).some(cell => cell.type.name !== "tableHeader" || hasSpan(cell) || cell.childCount > 1)) {
    return false
  }

  if (bodyRows.some(row =>
    childNodes(row).some(cell => cell.type.name === "tableHeader" || hasSpan(cell) || cell.childCount > 1)
  )) {
    return false
  }

  return true
}

/** Formats a raw HTML block per the commonmark HTML-block spec (blank line padding). */
function serializeAsHtmlBlock(node) {
  const schema = node.type.schema
  const html = getHTMLFromFragment(Fragment.from(node), schema)
  const wrapper = new globalThis.DOMParser().parseFromString(`<body>${html}</body>`, "text/html").body
  const element = wrapper.firstElementChild
  element.innerHTML = element.innerHTML.trim() ? `\n${element.innerHTML}\n` : "\n"
  return `\n${element.outerHTML}\n`
}

/** GFM delimiter cell for a column, honoring alignment (:---, ---:, :---:, ---). */
function delimiterCell(align) {
  if (align === "left") return ":---"
  if (align === "right") return "---:"
  if (align === "center") return ":---:"
  return "---"
}

/**
 * Extends @tiptap/extension-table's markdown serialization to round-trip GFM
 * column alignment. Parsing already works out of the box: markdown-it's table
 * rule writes `style="text-align:..."` on <th>/<td> for `:---`/`---:`/`:---:`
 * delimiters, and TableHeader/TableCell's own `align` attribute already parses
 * that back (see node_modules/@tiptap/extension-table). Only the markdown
 * *serializer* was lossy — tiptap-markdown's bundled Table node hardcodes a
 * plain `--- | ---` delimiter row regardless of alignment.
 */
export const TableMarkdown = Table.extend({
  addStorage() {
    return {
      markdown: {
        serialize(state, node, parent) {
          if (!isMarkdownSerializable(node)) {
            state.write(serializeAsHtmlBlock(node))
            state.closeBlock(node)
            return
          }

          state.inTable = true
          const headerRow = node.firstChild
          const aligns = headerRow ? childNodes(headerRow).map(cell => cell.attrs.align ?? null) : []

          node.forEach((row, _rowPos, rowIndex) => {
            state.write("| ")
            row.forEach((col, _colPos, colIndex) => {
              if (colIndex) state.write(" | ")
              const cellContent = col.firstChild
              if (cellContent?.textContent.trim()) {
                state.renderInline(cellContent)
              }
            })
            state.write(" |")
            state.ensureNewLine()
            if (!rowIndex) {
              const delimiterRow = aligns.map(delimiterCell).join(" | ")
              state.write(`| ${delimiterRow} |`)
              state.ensureNewLine()
            }
          })
          state.closeBlock(node)
          state.inTable = false
        },
        parse: {
          // handled by markdown-it
        },
      },
    }
  },
})
